// Validation functions
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone) => {
  // Updated phone validation - accepts various formats without requiring country code
  // Accepts: (123) 456-7890, 123-456-7890, 123.456.7890, 1234567890, +1234567890
  const phoneRegex =
    /^(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;
  return phoneRegex.test(phone.replace(/\s+/g, ""));
};

export const validateZipCode = (zipCode) => {
  // US ZIP code validation (5 digits or 5+4 format)
  const zipRegex = /^\d{5}(-\d{4})?$/;
  return zipRegex.test(zipCode);
};

export const validateRequired = (value, fieldName) => {
  if (!value || value.trim() === "") {
    return `${fieldName} is required`;
  }
  return null;
};

// Form validation function
export const validateForm = (formData) => {
  const errors = {};

  // Required field validations
  const requiredValidation = validateRequired(
    formData.first_name,
    "First Name"
  );
  if (requiredValidation) errors.first_name = requiredValidation;

  const lastNameValidation = validateRequired(formData.last_name, "Last Name");
  if (lastNameValidation) errors.last_name = lastNameValidation;

  const phoneValidation = validateRequired(formData.phone, "Phone");
  if (phoneValidation) {
    errors.phone = phoneValidation;
  } else if (!validatePhone(formData.phone)) {
    errors.phone = "Please enter a valid phone number (e.g., (123) 456-7890)";
  }

  // Required Company fields
  const companyNameValidation = validateRequired(
    formData.company_name,
    "Company Name"
  );
  if (companyNameValidation) errors.company_name = companyNameValidation;

  const companyPhoneValidation = validateRequired(
    formData.company_phone,
    "Company Phone"
  );
  if (companyPhoneValidation) {
    errors.company_phone = companyPhoneValidation;
  } else if (!validatePhone(formData.company_phone)) {
    errors.company_phone =
      "Please enter a valid company phone number (e.g., (123) 456-7890)";
  }

  // Email validation (if provided)
  if (formData.email && !validateEmail(formData.email)) {
    errors.email = "Please enter a valid email address";
  }

  // Company email validation (if provided)
  if (formData.company_email && !validateEmail(formData.company_email)) {
    errors.company_email = "Please enter a valid company email address";
  }

  // Address validations (if any address field is filled, validate required fields)
  const hasUserAddress = Object.values(formData.user_address).some(
    (value) => value.trim() !== ""
  );
  if (hasUserAddress) {
    if (!formData.user_address.address_line1.trim()) {
      errors["user_address.address_line1"] =
        "Address Line 1 is required when providing address";
    }
    if (!formData.user_address.city.trim()) {
      errors["user_address.city"] = "City is required when providing address";
    }
    if (!formData.user_address.state.trim()) {
      errors["user_address.state"] = "State is required when providing address";
    }
    if (!formData.user_address.zip_code.trim()) {
      errors["user_address.zip_code"] =
        "ZIP code is required when providing address";
    } else if (!validateZipCode(formData.user_address.zip_code)) {
      errors["user_address.zip_code"] =
        "Please enter a valid ZIP code (12345 or 12345-6789)";
    }
  }

  // Company address validations
  const hasCompanyAddress = Object.values(formData.company_address).some(
    (value) => value.trim() !== ""
  );
  if (hasCompanyAddress) {
    if (!formData.company_address.address_line1.trim()) {
      errors["company_address.address_line1"] =
        "Company Address Line 1 is required when providing company address";
    }
    if (!formData.company_address.city.trim()) {
      errors["company_address.city"] =
        "Company City is required when providing company address";
    }
    if (!formData.company_address.state.trim()) {
      errors["company_address.state"] =
        "Company State is required when providing company address";
    }
    if (!formData.company_address.zip_code.trim()) {
      errors["company_address.zip_code"] =
        "Company ZIP code is required when providing company address";
    } else if (!validateZipCode(formData.company_address.zip_code)) {
      errors["company_address.zip_code"] =
        "Please enter a valid company ZIP code";
    }
  }

  // Date validations
  if (formData.expiry_date) {
    const expiryDate = new Date(formData.expiry_date);
    const today = new Date();
    if (expiryDate < today) {
      errors.expiry_date = "License expiry date cannot be in the past";
    }
  }

  return errors;
};

// Helper function to convert empty strings to null (except for required fields)
export const sanitizeFormData = (data) => {
  const sanitized = {};

  // Required fields that should not be converted to null
  const requiredFields = [
    "first_name",
    "last_name",
    "phone",
    "company_name",
    "company_phone",
  ];

  // Required address fields for user_address
  const requiredAddressFields = ["zip_code", "address_line1", "city", "state"];

  // Required company address fields
  const requiredCompanyAddressFields = [
    "zip_code",
    "address_line1",
    "city",
    "state",
  ];

  for (const [key, value] of Object.entries(data)) {
    if (key === "timezone") {
      // Handle timezone - nest it inside settings object
      if (value && value.trim() !== "") {
        sanitized.settings = {
          timezone: value
        };
      }
      // Skip adding timezone as a top-level field
      continue;
    } else if (key === "user_address" && value && typeof value === "object") {
      // Handle user_address nested object
      const hasAnyAddressValue = Object.values(value).some(
        (val) => val && val.trim() !== ""
      );

      if (hasAnyAddressValue) {
        // If any address field has a value, keep all fields as strings (required for validation)
        sanitized[key] = {};
        for (const [addressKey, addressValue] of Object.entries(value)) {
          sanitized[key][addressKey] = addressValue || "";
        }
      } else {
        // If no address fields have values, set all to null
        sanitized[key] = {
          zip_code: null,
          address_line1: null,
          address_line2: null,
          city: null,
          state: null,
        };
      }
    } else if (
      key === "company_address" &&
      value &&
      typeof value === "object"
    ) {
      // Handle company_address nested object
      const hasAnyCompanyAddressValue = Object.values(value).some(
        (val) => val && val.trim() !== ""
      );

      if (hasAnyCompanyAddressValue) {
        // If any company address field has a value, keep all fields as strings
        sanitized[key] = {};
        for (const [addressKey, addressValue] of Object.entries(value)) {
          sanitized[key][addressKey] = addressValue || "";
        }
      } else {
        // If no company address fields have values, set all to null
        sanitized[key] = {
          zip_code: null,
          address_line1: null,
          address_line2: null,
          city: null,
          state: null,
        };
      }
    } else {
      // Handle regular fields
      if (typeof value === "string") {
        if (requiredFields.includes(key)) {
          sanitized[key] = value || "";
        } else {
          sanitized[key] = value === "" ? null : value;
        }
      } else {
        // For non-string values (like numbers, booleans, null), keep as is
        sanitized[key] = value;
      }
    }
  }

  return sanitized;
};

// Helper function to render error message
export const renderError = (error) => {
  if (!error) return null;

  if (typeof error === "string") {
    return error;
  }

  if (typeof error === "object") {
    // Handle validation errors from API
    if (error.message) {
      return error.message;
    }

    // Handle field-specific errors
    const errorMessages = [];
    for (const [field, messages] of Object.entries(error)) {
      if (Array.isArray(messages)) {
        errorMessages.push(`${field}: ${messages.join(", ")}`);
      } else if (typeof messages === "string") {
        errorMessages.push(`${field}: ${messages}`);
      }
    }

    return errorMessages.length > 0
      ? errorMessages.join("; ")
      : "An error occurred";
  }

  return "An unexpected error occurred";
};

// Initial form data structure
export const getInitialFormData = () => ({
  email: "",
  first_name: "",
  last_name: "",
  phone: "",
  timezone: "",
  profile_image: null,
  user_address: {
    zip_code: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
  },
  license_number: "",
  state_issued: "",
  expiry_date: "",
  note: "",
  company_name: "",
  company_phone: "",
  company_email: "",
  company_profile_image: null,
  company_state_issued: "",
  company_expiry_date: "",
  company_address: {
    zip_code: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
  },
});

// Function to get user's timezone from browser with fallback
export const getUserTimezone = () => {
  try {
    // Try to get timezone from browser
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return browserTimezone;
  } catch (error) {
    // Fall back to Pacific timezone if browser timezone is inaccessible
    console.warn("Could not detect user timezone, falling back to Pacific Time");
    return "America/Los_Angeles";
  }
};

// Profile to form data mapping
export const mapProfileToFormData = (profile) => {
  if (!profile) return getInitialFormData();

  return {
    email: profile.email || "",
    first_name: profile.first_name || "",
    last_name: profile.last_name || "",
    phone: profile.phone || "",
    timezone: profile.settings?.timezone || "",
    profile_image: profile.profile_image?.id || null,
    license_number: profile.license_number || "",
    state_issued: profile.state_issued || "",
    expiry_date: profile.expiry_date || "",
    note: profile.note || "",
    user_address: {
      zip_code: profile.address?.postal_code || "",
      address_line1: profile.address?.address_line1 || "",
      address_line2: profile.address?.address_line2 || "",
      city: profile.address?.locality || "",
      state: profile.address?.administrative_area || "",
    },
    company_name: profile.company?.name || "",
    company_phone: profile.company?.phone || "",
    company_email: profile.company?.email || "",
    company_profile_image: profile.company?.profile_image?.id || null,
    company_state_issued: "",
    company_expiry_date: "",
    company_address: {
      zip_code: profile.company?.address?.postal_code || "",
      address_line1: profile.company?.address?.address_line1 || "",
      address_line2: profile.company?.address?.address_line2 || "",
      city: profile.company?.address?.locality || "",
      state: profile.company?.address?.administrative_area || "",
    },
  };
};
